import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, MenuItem, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tab, Tabs,
  TextField, Typography, Grid,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import AssessmentIcon from '@mui/icons-material/Assessment';
import * as XLSX from 'xlsx';
import config from '../../../config';

const REPORTS = [
  { key: 'salaryRegister',  label: 'Salary Register',     monthScoped: true },
  { key: 'attendance',      label: 'Attendance Summary',  monthScoped: true },
  { key: 'leave',           label: 'Leave Report',        rangeScoped: true },
  { key: 'overtime',        label: 'Overtime Report',     rangeScoped: true },
  { key: 'advance',         label: 'Advance Report' },
  { key: 'loan',            label: 'Loan Report' },
  { key: 'payments',        label: 'Salary Payments',     monthScoped: true },
  { key: 'departmentCost',  label: 'Department Cost',     monthScoped: true },
  { key: 'annualSummary',   label: 'Annual Summary',      annual: true },
];

// HR Reports — multi-tab hub. Each tab hits an existing or report-specific
// endpoint, renders a uniform table, and exposes Excel + Print export.
// Keeping all reports here (vs separate pages) means filters + export
// buttons live in one place and stay consistent.
export default function HrReports() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const companyId = company?.id;
  const now = new Date();
  const monthStart = (() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); })();
  const today = now.toISOString().slice(0, 10);

  const [activeTab, setActiveTab] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [empFilter, setEmpFilter] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);   // Annual summary aggregates

  const cfg = REPORTS[activeTab];

  const loadRefs = async () => {
    const [d, e] = await Promise.all([
      fetch(`${config.API_BASE_URL}/Hr/Departments/${companyId}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.ok ? r.json() : []),
      fetch(`${config.API_BASE_URL}/Hr/Employees/${companyId}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.ok ? r.json() : []),
    ]);
    setDepartments(d); setEmployees(e);
  };

  useEffect(() => {
    if (companyId) loadRefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // ── Per-tab loader ────────────────────────────────────────────────
  const load = async () => {
    if (!companyId) return;
    setLoading(true); setRows([]); setSummary(null);
    try {
      let url;
      const qs = new URLSearchParams();
      switch (cfg.key) {
        case 'salaryRegister':
          qs.append('month', month); qs.append('year', year);
          if (empFilter) qs.append('employeeId', empFilter);
          if (departmentFilter) qs.append('departmentId', departmentFilter);
          url = `${config.API_BASE_URL}/Hr/Payroll/${companyId}?${qs.toString()}`;
          break;
        case 'attendance':
          qs.append('month', month); qs.append('year', year);
          if (departmentFilter) qs.append('departmentId', departmentFilter);
          url = `${config.API_BASE_URL}/Hr/Attendance/MonthlySummary/${companyId}?${qs.toString()}`;
          break;
        case 'leave':
          if (empFilter) qs.append('employeeId', empFilter);
          qs.append('from', fromDate); qs.append('to', toDate);
          url = `${config.API_BASE_URL}/Hr/LeaveRequests/${companyId}?${qs.toString()}`;
          break;
        case 'overtime':
          if (empFilter) qs.append('employeeId', empFilter);
          qs.append('from', fromDate); qs.append('to', toDate);
          url = `${config.API_BASE_URL}/Hr/Overtime/${companyId}?${qs.toString()}`;
          break;
        case 'advance':
          if (empFilter) qs.append('employeeId', empFilter);
          url = `${config.API_BASE_URL}/Hr/Advances/${companyId}?${qs.toString()}`;
          break;
        case 'loan':
          if (empFilter) qs.append('employeeId', empFilter);
          url = `${config.API_BASE_URL}/Hr/Loans/${companyId}?${qs.toString()}`;
          break;
        case 'payments':
          qs.append('month', month); qs.append('year', year);
          if (empFilter) qs.append('employeeId', empFilter);
          url = `${config.API_BASE_URL}/Hr/SalaryPayments/${companyId}?${qs.toString()}`;
          break;
        case 'departmentCost':
          qs.append('month', month); qs.append('year', year);
          url = `${config.API_BASE_URL}/Hr/Reports/DepartmentCost/${companyId}?${qs.toString()}`;
          break;
        case 'annualSummary':
          qs.append('year', year);
          url = `${config.API_BASE_URL}/Hr/Reports/AnnualSummary/${companyId}?${qs.toString()}`;
          break;
        default: return;
      }
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (cfg.key === 'annualSummary') {
        setSummary(data);
        setRows(data.months || []);
      } else {
        setRows(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, companyId, month, year, fromDate, toDate, departmentFilter, empFilter]);

  // ── Column maps per report (also drive Excel export columns) ──────
  const columns = useMemo(() => {
    const inr = (v) => Number(v || 0).toLocaleString('en-IN');
    switch (cfg.key) {
      case 'salaryRegister':
        return [
          { k: 'employeeCode',   h: 'Code' },
          { k: 'employeeName',   h: 'Name' },
          { k: 'departmentName', h: 'Department' },
          { k: 'basicSalary',    h: 'Basic', fmt: inr, align: 'right' },
          { k: 'overtimeAmount', h: 'OT', fmt: inr, align: 'right' },
          { k: 'totalEarnings',  h: 'Gross', fmt: inr, align: 'right' },
          { k: 'totalDeductions',h: 'Deductions', fmt: inr, align: 'right' },
          { k: 'netSalary',      h: 'Net', fmt: inr, align: 'right' },
          { k: 'paidAmount',     h: 'Paid', fmt: inr, align: 'right' },
          { k: 'balancePayable', h: 'Balance', fmt: inr, align: 'right' },
          { k: 'status',         h: 'Status' },
        ];
      case 'attendance':
        return [
          { k: 'employeeCode',     h: 'Code' },
          { k: 'employeeName',     h: 'Name' },
          { k: 'departmentName',   h: 'Department' },
          { k: 'presentDays',      h: 'Present', align: 'right' },
          { k: 'absentDays',       h: 'Absent', align: 'right' },
          { k: 'halfDays',         h: 'Half', align: 'right' },
          { k: 'leaveDays',        h: 'Leave', align: 'right' },
          { k: 'weeklyOffDays',    h: 'Weekly Off', align: 'right' },
          { k: 'holidayDays',      h: 'Holiday', align: 'right' },
          { k: 'totalWorkingHours',h: 'Hours', align: 'right' },
          { k: 'totalLateMinutes', h: 'Late (min)', align: 'right' },
        ];
      case 'leave':
        return [
          { k: 'employeeCode',  h: 'Code' },
          { k: 'employeeName',  h: 'Name' },
          { k: 'leaveTypeName', h: 'Type' },
          { k: 'fromDate',      h: 'From', fmt: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '' },
          { k: 'toDate',        h: 'To',   fmt: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '' },
          { k: 'days',          h: 'Days', align: 'right' },
          { k: 'status',        h: 'Status' },
          { k: 'reason',        h: 'Reason' },
        ];
      case 'overtime':
        return [
          { k: 'otDate',       h: 'Date', fmt: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '' },
          { k: 'employeeCode', h: 'Code' },
          { k: 'employeeName', h: 'Name' },
          { k: 'departmentName', h: 'Department' },
          { k: 'otHours',      h: 'Hours', align: 'right' },
          { k: 'otRate',       h: 'Rate', fmt: inr, align: 'right' },
          { k: 'otAmount',     h: 'Amount', fmt: inr, align: 'right' },
        ];
      case 'advance':
        return [
          { k: 'advanceDate',     h: 'Date', fmt: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '' },
          { k: 'employeeCode',    h: 'Code' },
          { k: 'employeeName',    h: 'Name' },
          { k: 'advanceAmount',   h: 'Amount', fmt: inr, align: 'right' },
          { k: 'recoveredAmount', h: 'Recovered', fmt: inr, align: 'right' },
          { k: 'pendingAmount',   h: 'Pending', fmt: inr, align: 'right' },
          { k: 'recoveryMonths',  h: 'Months', align: 'right' },
          { k: 'status',          h: 'Status' },
        ];
      case 'loan':
        return [
          { k: 'startMonth',       h: 'Start', fmt: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '' },
          { k: 'employeeCode',     h: 'Code' },
          { k: 'employeeName',     h: 'Name' },
          { k: 'loanAmount',       h: 'Loan', fmt: inr, align: 'right' },
          { k: 'emiAmount',        h: 'EMI', fmt: inr, align: 'right' },
          { k: 'totalMonths',      h: 'Tenure (mo)', align: 'right' },
          { k: 'paidAmount',       h: 'Paid', fmt: inr, align: 'right' },
          { k: 'remainingBalance', h: 'Outstanding', fmt: inr, align: 'right' },
          { k: 'status',           h: 'Status' },
        ];
      case 'payments':
        return [
          { k: 'paymentDate',  h: 'Date', fmt: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '' },
          { k: 'employeeCode', h: 'Code' },
          { k: 'employeeName', h: 'Name' },
          { k: 'payMode',      h: 'Mode' },
          { k: 'referenceNo',  h: 'Reference' },
          { k: 'bankName',     h: 'Bank' },
          { k: 'amount',       h: 'Amount', fmt: inr, align: 'right' },
          { k: 'paidByName',   h: 'Paid By' },
        ];
      case 'departmentCost':
        return [
          { k: 'departmentName',  h: 'Department' },
          { k: 'employeeCount',   h: 'Staff', align: 'right' },
          { k: 'totalEarnings',   h: 'Gross', fmt: inr, align: 'right' },
          { k: 'totalDeductions', h: 'Deductions', fmt: inr, align: 'right' },
          { k: 'netSalary',       h: 'Net', fmt: inr, align: 'right' },
          { k: 'overtimeAmount',  h: 'OT', fmt: inr, align: 'right' },
        ];
      case 'annualSummary':
        return [
          { k: 'monthName',     h: 'Month' },
          { k: 'employeeCount', h: 'Staff', align: 'right' },
          { k: 'gross',         h: 'Gross', fmt: inr, align: 'right' },
          { k: 'net',           h: 'Net', fmt: inr, align: 'right' },
          { k: 'overtime',      h: 'OT', fmt: inr, align: 'right' },
          { k: 'paid',          h: 'Paid', fmt: inr, align: 'right' },
        ];
      default: return [];
    }
  }, [cfg.key]);

  // ── Excel + Print ─────────────────────────────────────────────────
  const exportExcel = () => {
    if (!rows.length) return;
    const data = rows.map((r) => {
      const out = {};
      columns.forEach((c) => {
        const v = r[c.k];
        out[c.h] = c.fmt ? c.fmt(v) : (v == null ? '' : v);
      });
      return out;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, cfg.label.slice(0, 30));
    const fname = `${cfg.label.replace(/\s+/g, '_')}_${Date.now()}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  const printReport = () => {
    const head = `<tr>${columns.map((c) => `<th style="text-align:${c.align || 'left'}">${c.h}</th>`).join('')}</tr>`;
    const body = rows.map((r) => `<tr>${columns.map((c) => {
      const v = r[c.k];
      const s = c.fmt ? c.fmt(v) : (v == null ? '' : v);
      return `<td style="text-align:${c.align || 'left'}">${String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')}</td>`;
    }).join('')}</tr>`).join('');
    const titleScope = cfg.monthScoped
      ? `${new Date(year, month - 1, 1).toLocaleString('en-IN', { month: 'long' })} ${year}`
      : cfg.rangeScoped
        ? `${new Date(fromDate).toLocaleDateString('en-IN')} – ${new Date(toDate).toLocaleDateString('en-IN')}`
        : cfg.annual ? `Year ${year}` : '';
    const html = `<!DOCTYPE html><html><head><title>${cfg.label}</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font-family: Arial, sans-serif; color:#000; }
        h1 { font-size: 16px; margin: 0; }
        h2 { font-size: 12px; margin: 4px 0 12px 0; color: #555; font-weight: normal; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { padding: 6px 8px; border: 1px solid #ccc; }
        thead th { background: #f1f5f9; font-weight: 700; }
        tr:nth-child(even) td { background: #fafbfc; }
      </style></head><body>
      <h1>${cfg.label}</h1>
      <h2>${company?.name || ''} · ${titleScope}</h2>
      <table>${head}${body}</table>
      </body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow || iframe.contentDocument;
    doc.document.open(); doc.document.write(html); doc.document.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 400);
  };

  // ── Filter strip per report ───────────────────────────────────────
  const filterStrip = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} flexWrap='wrap'>
      {cfg.monthScoped && (
        <>
          <TextField select size='small' label='Month' value={month}
            onChange={(e) => setMonth(e.target.value)} sx={{ bgcolor: '#fff', minWidth: 140 }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <MenuItem key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('en-IN', { month: 'long' })}</MenuItem>
            ))}
          </TextField>
          <TextField type='number' size='small' label='Year' value={year}
            onChange={(e) => setYear(Number(e.target.value))} sx={{ bgcolor: '#fff', width: 110 }} />
        </>
      )}
      {cfg.annual && (
        <TextField type='number' size='small' label='Year' value={year}
          onChange={(e) => setYear(Number(e.target.value))} sx={{ bgcolor: '#fff', width: 110 }} />
      )}
      {cfg.rangeScoped && (
        <>
          <TextField type='date' size='small' label='From' InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={{ bgcolor: '#fff' }} />
          <TextField type='date' size='small' label='To' InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)} sx={{ bgcolor: '#fff' }} />
        </>
      )}
      {['salaryRegister', 'attendance', 'departmentCost'].includes(cfg.key) && (
        <TextField select size='small' label='Department' value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)} sx={{ bgcolor: '#fff', minWidth: 180 }}>
          <MenuItem value=''>All</MenuItem>
          {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.departmentName}</MenuItem>)}
        </TextField>
      )}
      {!['departmentCost', 'annualSummary'].includes(cfg.key) && (
        <TextField select size='small' label='Employee' value={empFilter}
          onChange={(e) => setEmpFilter(e.target.value)} sx={{ bgcolor: '#fff', minWidth: 220 }}>
          <MenuItem value=''>All</MenuItem>
          {employees.map((e) => <MenuItem key={e.id} value={e.id}>{e.employeeName} ({e.employeeCode})</MenuItem>)}
        </TextField>
      )}
      <Box sx={{ flex: 1 }} />
      <Button variant='outlined' startIcon={<FileDownloadIcon />}
        onClick={exportExcel} disabled={rows.length === 0}>
        Excel
      </Button>
      <Button variant='outlined' startIcon={<PrintIcon />}
        onClick={printReport} disabled={rows.length === 0}>
        Print / PDF
      </Button>
    </Stack>
  );

  // Compute footer totals for numeric columns
  const footerTotals = useMemo(() => {
    const totals = {};
    columns.forEach((c) => {
      if (c.align === 'right' && c.fmt) {
        const sum = rows.reduce((a, r) => a + Number(r[c.k] || 0), 0);
        if (sum > 0) totals[c.k] = sum;
      }
    });
    return totals;
  }, [rows, columns]);
  const hasTotals = Object.keys(footerTotals).length > 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: '#0d9488', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AssessmentIcon />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem' }}>HR Reports</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>
            Salary register · Attendance · Leave · OT · Advances · Loans · Payments · Department cost · Annual summary
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant='scrollable' scrollButtons='auto'>
          {REPORTS.map((r, i) => <Tab key={r.key} label={r.label} />)}
        </Tabs>
      </Card>

      {filterStrip}

      {/* Annual summary KPI strip */}
      {cfg.key === 'annualSummary' && summary && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            ['Total Gross', summary.totalGross, '#16a34a'],
            ['Total Net', summary.totalNet, '#1A56DB'],
            ['Total OT', summary.totalOvertime, '#f97316'],
            ['Total Paid', summary.totalPaid, '#0d9488'],
          ].map(([label, val, color]) => (
            <Grid item xs={6} sm={3} key={label}>
              <Card sx={{ borderLeft: `4px solid ${color}`, p: 1.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>{label}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                  ₹{Number(val).toLocaleString('en-IN')}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    {columns.map((c) => (
                      <TableCell key={c.k} align={c.align || 'left'} sx={{ fontWeight: 700 }}>
                        {c.h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>No data for selected filters.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r, i) => (
                    <TableRow key={r.id ?? i} hover>
                      {columns.map((c) => (
                        <TableCell key={c.k} align={c.align || 'left'}
                          sx={{ fontWeight: c.k === 'employeeName' || c.k === 'departmentName' || c.k === 'monthName' ? 600 : 400 }}>
                          {(() => {
                            const v = r[c.k];
                            if (c.k === 'status') {
                              const colorMap = { Active: '#92400e', Approved: '#166534', Rejected: '#991b1b', Pending: '#92400e', Paid: '#166534', Draft: '#92400e', Processed: '#1e40af', Closed: '#475569', Cancelled: '#475569' };
                              return <Chip size='small' label={v || '—'} sx={{ bgcolor: '#f1f5f9', color: colorMap[v] || '#475569', fontWeight: 600 }} />;
                            }
                            return c.fmt ? c.fmt(v) : (v ?? '—');
                          })()}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {hasTotals && (
                    <TableRow sx={{ bgcolor: '#f8fafc', borderTop: '2px solid #cbd5e1' }}>
                      {columns.map((c, i) => (
                        <TableCell key={c.k} align={c.align || 'left'} sx={{ fontWeight: 800 }}>
                          {i === 0 ? 'TOTAL' : (footerTotals[c.k] != null ? c.fmt(footerTotals[c.k]) : '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
